import type { StorefrontUpsell } from '@xshop/shared';
import { useShippingQuote } from './useShippingQuote';
import { useDiscountCode } from './useDiscountCode';
import { DISCOUNT_CODE_WORDS } from './discountCodeWords';
import type { CheckoutLanguage } from './checkoutLabels';

// What the COD form's order comes to. Every number is the server's: guessing any of them here is what let the page
// quote 349 and charge 384. The code is checked against the merchandise alone, as the order prices it — never the bump.

/** The one country the COD form delivers to, which the delivery quote and the order are priced for. */
export const CHECKOUT_COUNTRY = 'MA';

/** What the money hook needs: the merchandise as priced, the bump, and the lines a discount code is checked against. */
type MoneyInput = {
  lineTotal: number;
  acceptedOffer: StorefrontUpsell | null;
  /** True in the builder and a theme preview, where no code is checked. */
  preview: boolean;
  codeLines: Array<{ productId: string; quantity: number; lineTotal: number }>;
  language: CheckoutLanguage;
};

// Reads the delivery, the discount and the total, and the discount's row as the summary names it.
export function useCheckoutMoney({ lineTotal, acceptedOffer, preview, codeLines, language }: MoneyInput) {
  const subtotal = lineTotal + (acceptedOffer?.unitPrice ?? 0);
  const shippingFee = useShippingQuote(subtotal, CHECKOUT_COUNTRY);
  const discount = useDiscountCode(preview ? null : { lineItems: codeLines, subtotal: lineTotal, shippingFee: shippingFee ?? 0 });
  const codeWords = DISCOUNT_CODE_WORDS[language];
  const totalPrice = shippingFee === null ? null : Math.max(0, subtotal + shippingFee - (discount.saving?.amount ?? 0));
  const discountRow = discount.saving
    ? { label: discount.saving.code ? codeWords.row.replace('{code}', discount.saving.code) : discount.saving.title, amount: discount.saving.amount }
    : null;
  return { shippingFee, discount, codeWords, totalPrice, discountRow };
}
